import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import AdminShell from "@/components/admin/AdminShell";

const STATUSES = ["new", "reviewing", "accepted", "declined"];
const CERT = { completed: "Completed certification", in_progress: "Enrolled / in progress", planning: "Planning to enroll" };
const TAROT = { yes: "Open to it", no: "Not open", completed: "Already completed" };

function Field({ k, v }) {
  return (
    <div>
      <p className="overline-plum mb-1">{k}</p>
      <p className="text-sm text-aviana-inkSoft whitespace-pre-line">{v || "—"}</p>
    </div>
  );
}

function AppCard({ a, onStatus }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card-aviana p-6" data-testid={`trainer-app-${a.id}`}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <button onClick={() => setOpen((o) => !o)} className="text-left" data-testid={`trainer-app-toggle-${a.id}`}>
          <h3 className="font-display text-2xl text-aviana-ink inline-flex items-center gap-2">
            {a.full_name} <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </h3>
          <p className="text-sm text-aviana-muted">{a.email} · {CERT[a.certification_status]} · {new Date(a.created_at).toLocaleDateString()}</p>
        </button>
        <select value={a.status} onChange={(e) => onStatus(a, e.target.value)} data-testid={`trainer-app-status-${a.id}`}
          className="bg-white border border-aviana-border rounded-full px-4 py-2 text-[12px] tracking-[0.12em] uppercase text-aviana-plum outline-none">
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {open && (
        <div className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-5" data-testid={`trainer-app-details-${a.id}`}>
          <Field k="Certification date / timeline" v={a.certification_date} />
          <Field k="Teaching aerial yoga for" v={a.years_teaching} />
          <Field k="Teaching context" v={[...(a.teaching_context || []), a.teaching_context_other].filter(Boolean).join(", ")} />
          <Field k="Studio owner" v={a.studio_owner} />
          <Field k="Classes per month" v={a.classes_per_month} />
          <Field k="Liability insurance" v={a.has_insurance} />
          <Field k="Mythical Goddess Tarot course" v={TAROT[a.tarot_course]} />
          <Field k="Where they'd bring a training" v={a.bring_training_where} />
          <div className="sm:col-span-2"><Field k="What draws them to the Aviana lineage" v={a.drawn_to_lineage} /></div>
          <div className="sm:col-span-2"><Field k="Hope to contribute" v={a.hope_to_contribute} /></div>
          <div className="sm:col-span-2"><Field k="Anything else" v={a.anything_else} /></div>
        </div>
      )}
    </div>
  );
}

export default function AdminTrainerApplications() {
  const [rows, setRows] = useState(null);
  const load = async () => {
    try { setRows((await api.get("/admin/trainer-applications")).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not load applications"); setRows([]); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  const onStatus = async (a, status) => {
    try {
      await api.patch(`/admin/trainer-applications/${a.id}`, { status });
      setRows((r) => r.map((x) => (x.id === a.id ? { ...x, status } : x)));
      toast.success(`Marked ${status}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
  };
  return (
    <AdminShell>
      <div className="max-w-5xl" data-testid="admin-trainer-applications">
        <p className="overline-plum mb-2">Join the Collective</p>
        <h1 className="font-display text-4xl md:text-5xl text-aviana-ink mb-3">Trainer Applications</h1>
        <p className="text-aviana-muted mb-10">Applications from the Become an Aerial Yoga Trainer page. Each one is also emailed to you.</p>
        {rows === null ? <p className="text-aviana-muted">Loading…</p> : rows.length === 0 ? (
          <p className="text-aviana-muted" data-testid="trainer-apps-empty">No applications yet.</p>
        ) : (
          <div className="space-y-4">{rows.map((a) => <AppCard key={a.id} a={a} onStatus={onStatus} />)}</div>
        )}
      </div>
    </AdminShell>
  );
}
